// ============================================================
// Bannière offline — Lot L21 du plan SaaS
// ============================================================
//
// Affiche l'état réseau (online/offline) + le nombre d'opérations
// en attente dans la file IndexedDB. Au retour du réseau, déclenche
// automatiquement un flush.
// ============================================================
import React, { useEffect, useState } from 'react';
import { WifiOff, Wifi, UploadCloud, AlertCircle } from 'lucide-react';
import api from '../../services/api';
import { pendingCount, flush } from '../../utils/offline-queue';
import toast from 'react-hot-toast';

export default function OfflineBanner() {
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [enAttente, setEnAttente] = useState(0);
  const [syncEnCours, setSyncEnCours] = useState(false);

  // Compteur live
  const refreshCount = async () => {
    try { setEnAttente(await pendingCount()); } catch {}
  };

  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, 5000);
    return () => clearInterval(id);
  }, []);

  // Détection changement de réseau
  useEffect(() => {
    const onUp = async () => {
      setOnline(true);
      if (await pendingCount() > 0) {
        setSyncEnCours(true);
        try {
          const r = await flush(api);
          await refreshCount();
          if (r.succes > 0) toast.success(`${r.succes} opération(s) synchronisée(s)`);
          if (r.echecs > 0) toast.error(`${r.echecs} opération(s) en échec — réessai prévu`);
        } finally {
          setSyncEnCours(false);
        }
      }
    };
    const onDown = () => setOnline(false);
    window.addEventListener('online', onUp);
    window.addEventListener('offline', onDown);
    return () => {
      window.removeEventListener('online', onUp);
      window.removeEventListener('offline', onDown);
    };
  }, []);

  if (online && enAttente === 0 && !syncEnCours) return null;

  if (!online) {
    return (
      <div className="bg-orange-500/15 border-b border-orange-500/40 px-4 py-2 text-sm text-orange-200 flex items-center gap-2">
        <WifiOff size={16} className="text-orange-400" />
        <span className="font-medium">Mode hors-ligne</span>
        <span className="text-orange-300/80">
          — tes ventes / dépenses / sorties terrain seront synchronisées au retour du réseau.
        </span>
        {enAttente > 0 && (
          <span className="ml-auto bg-orange-900/40 border border-orange-700/50 rounded-full px-2 py-0.5 text-xs">
            {enAttente} en attente
          </span>
        )}
      </div>
    );
  }

  if (syncEnCours) {
    return (
      <div className="bg-blue-500/15 border-b border-blue-500/40 px-4 py-2 text-sm text-blue-200 flex items-center gap-2">
        <UploadCloud size={16} className="text-blue-400 animate-pulse" />
        <span>Synchronisation en cours… ({enAttente} restantes)</span>
      </div>
    );
  }

  // Online + il reste des ops en file (= échecs récents)
  return (
    <div className="bg-amber-500/15 border-b border-amber-500/40 px-4 py-2 text-sm text-amber-200 flex items-center gap-2">
      <AlertCircle size={16} className="text-amber-400" />
      <span>{enAttente} opération(s) en attente de synchronisation</span>
      <button
        onClick={async () => {
          setSyncEnCours(true);
          try {
            const r = await flush(api);
            await refreshCount();
            if (r.succes > 0) toast.success(`${r.succes} synchronisée(s)`);
            if (r.echecs > 0) toast.error(`${r.echecs} en échec`);
          } finally {
            setSyncEnCours(false);
          }
        }}
        className="ml-auto text-xs underline hover:text-amber-100">
        Réessayer
      </button>
    </div>
  );
}
